import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, API } from "../lib/api";
import { toast } from "sonner";
import { LogOut, Download, Trash2, Edit3, Plus, Save, X, Eye, EyeOff, Upload } from "lucide-react";

const TABS = ["Leads", "Sim Builder", "Portfolio", "Testimonials", "Savings", "Story"];

export default function Admin() {
  const nav = useNavigate();
  const [tab, setTab] = useState("Leads");
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setMe(r.data))
      .catch(() => nav("/admin/login"));
  }, [nav]);

  const logout = async () => {
    await api.post("/auth/logout");
    localStorage.removeItem("sg_token");
    nav("/admin/login");
  };

  if (!me) return null;

  return (
    <div data-testid="admin-page" className="min-h-screen">
      <header className="border-b border-neutral-900 sticky top-0 bg-black/90 backdrop-blur z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <div>
            <p className="font-mono-label text-[10px] text-neutral-500">Admin Console</p>
            <p className="font-display font-bold text-lg">The Sim Guys</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-500 hidden sm:block">{me.email}</span>
            <button
              onClick={async () => {
                if (!window.confirm("Reload site content from the latest defaults? This overwrites Portfolio, Testimonials, Sim Builder, Savings and Story with what's in the code. Leads are untouched.")) return;
                try {
                  await api.post("/admin/reseed");
                  toast.success("Content reloaded from defaults.");
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail) || "Reseed failed.");
                }
              }}
              data-testid="admin-reseed-btn"
              className="hidden sm:inline-flex items-center gap-2 text-xs border border-neutral-800 px-3 py-2 hover:border-neutral-600"
            >
              Reload defaults
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 text-xs border border-neutral-800 px-3 py-2 hover:border-neutral-600"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setTab(t)}
              className={`px-4 py-3 text-sm border-b-2 whitespace-nowrap ${
                tab === t
                  ? "border-[#F5F5F0] text-[#F5F5F0]"
                  : "border-transparent text-neutral-500 hover:text-[#F5F5F0]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
        {tab === "Leads" && <Leads />}
        {tab === "Sim Builder" && <SimOptionsManager />}
        {tab === "Portfolio" && <PortfolioManager />}
        {tab === "Testimonials" && <TestimonialsManager />}
        {tab === "Savings" && <SavingsEditor />}
        {tab === "Story" && <StoryEditor />}
      </main>
    </div>
  );
}

// ---- Leads ----
function Leads() {
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [space, setSpace] = useState("");
  const [status, setStatus] = useState("");

  const load = () => {
    const params = {};
    if (space) params.space_type = space;
    if (status) params.status = status;
    api.get("/admin/leads", { params }).then((r) => setLeads(r.data));
  };
  useEffect(load, [space, status]);

  const setLeadStatus = async (id, s) => {
    await api.patch(`/admin/leads/${id}`, { status: s });
    toast.success("Updated");
    load();
    if (selected?.id === id) setSelected({ ...selected, status: s });
  };
  const del = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/admin/leads/${id}`);
    setSelected(null);
    load();
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="font-display font-black text-3xl tracking-tighter">Inbound Leads</h1>
        <div className="flex flex-wrap gap-2">
          <select
            data-testid="lead-filter-space"
            value={space}
            onChange={(e) => setSpace(e.target.value)}
            className="bg-black border border-neutral-800 px-3 py-2 text-xs"
          >
            <option value="">All spaces</option>
            {["Basement", "Garage", "Shop", "Shed", "Commercial", "Other"].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <select
            data-testid="lead-filter-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="bg-black border border-neutral-800 px-3 py-2 text-xs"
          >
            <option value="">All status</option>
            {["new", "contacted", "quoted", "won", "lost"].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <a
            href={`${API}/admin/leads.csv`}
            data-testid="admin-export-csv-btn"
            className="inline-flex items-center gap-2 border border-[#F5F5F0] px-3 py-2 text-xs"
          >
            <Download size={14} /> Export CSV
          </a>
        </div>
      </div>
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 border border-neutral-900">
          <table className="w-full text-sm">
            <thead className="border-b border-neutral-900">
              <tr className="text-left text-neutral-500 font-mono-label text-[10px]">
                <th className="p-3">Name</th>
                <th className="p-3">City</th>
                <th className="p-3">Space</th>
                <th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr
                  key={l.id}
                  onClick={() => setSelected(l)}
                  data-testid={`lead-row-${l.id}`}
                  className={`border-b border-neutral-900 cursor-pointer hover:bg-neutral-950 ${
                    selected?.id === l.id ? "bg-neutral-950" : ""
                  }`}
                >
                  <td className="p-3 font-medium">{l.name}</td>
                  <td className="p-3 text-neutral-400">{l.location}</td>
                  <td className="p-3 text-neutral-400">{l.space_type}</td>
                  <td className="p-3">
                    <span className="font-mono-label text-[10px] px-2 py-1 border border-neutral-800">
                      {l.status}
                    </span>
                  </td>
                </tr>
              ))}
              {!leads.length && (
                <tr>
                  <td colSpan="4" className="p-8 text-center text-neutral-500 text-sm">
                    No leads yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <div className="border border-neutral-900 p-6" data-testid="lead-detail-panel">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="font-display font-bold text-xl">{selected.name}</p>
                  <p className="text-xs text-neutral-500 mt-1">
                    {new Date(selected.created_at).toLocaleString()}
                  </p>
                </div>
                <button onClick={() => del(selected.id)} className="text-neutral-500 hover:text-red-400">
                  <Trash2 size={16} />
                </button>
              </div>
              <dl className="space-y-2 text-sm">
                <Field k="Email" v={<a className="underline" href={`mailto:${selected.email}`}>{selected.email}</a>} />
                <Field k="Phone" v={<a className="underline" href={`tel:${selected.phone}`}>{selected.phone}</a>} />
                <Field k="Location" v={selected.location} />
                <Field k="Space" v={selected.space_type} />
                <Field k="Budget" v={selected.budget_range} />
              </dl>
              {selected.message && (
                <div className="mt-4 border-t border-neutral-900 pt-4">
                  <p className="font-mono-label text-[10px] text-neutral-500 mb-1">Message</p>
                  <p className="text-sm text-neutral-300 whitespace-pre-wrap">{selected.message}</p>
                </div>
              )}
              {selected.build_config?.items?.length > 0 && (
                <div className="mt-4 border-t border-neutral-900 pt-4">
                  <p className="font-mono-label text-[10px] text-neutral-500 mb-2">
                    Attached Build · ${selected.build_config.total_low?.toLocaleString()}–$
                    {selected.build_config.total_high?.toLocaleString()}
                  </p>
                  <ul className="space-y-1 text-xs">
                    {selected.build_config.items.map((i, idx) => (
                      <li key={idx} className="flex justify-between">
                        <span>{i.label} · {i.tier}</span>
                        <span className="text-neutral-500">${i.price?.toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {selected.space_dimensions && (
                <div className="mt-4 border-t border-neutral-900 pt-4" data-testid="lead-dims">
                  <p className="font-mono-label text-[10px] text-neutral-500 mb-2">Space Dimensions</p>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <DimBox k="Width" v={selected.space_dimensions.length_ft} unit="ft" />
                    <DimBox k="Depth" v={selected.space_dimensions.depth_ft} unit="ft" />
                    <DimBox
                      k="Height"
                      v={`${selected.space_dimensions.height_ft ?? "—"}' ${selected.space_dimensions.height_in ?? 0}"`}
                    />
                  </div>
                </div>
              )}
              {selected.space_photos?.length > 0 && (
                <div className="mt-4 border-t border-neutral-900 pt-4" data-testid="lead-photos">
                  <p className="font-mono-label text-[10px] text-neutral-500 mb-2">
                    Space Photos ({selected.space_photos.length})
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {selected.space_photos.map((p) => (
                      <a
                        key={p}
                        href={`${API.replace(/\/api$/, "")}/api/files/${p}`}
                        target="_blank"
                        rel="noreferrer"
                        className="aspect-square overflow-hidden border border-neutral-800 block"
                      >
                        <img
                          src={`${API.replace(/\/api$/, "")}/api/files/${p}`}
                          alt=""
                          className="w-full h-full object-cover hover:scale-105 transition-transform"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-6 flex flex-wrap gap-2">
                {["new", "contacted", "quoted", "won", "lost"].map((s) => (
                  <button
                    key={s}
                    data-testid={`lead-status-${s}`}
                    onClick={() => setLeadStatus(selected.id, s)}
                    className={`text-xs px-3 py-1.5 border ${
                      selected.status === s ? "bg-[#F5F5F0] text-black border-[#F5F5F0]" : "border-neutral-800"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-neutral-900 p-8 text-center text-neutral-500 text-sm">
              Select a lead to view details.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Field = ({ k, v }) => (
  <div className="flex justify-between gap-4">
    <dt className="text-neutral-500">{k}</dt>
    <dd className="text-[#F5F5F0] text-right">{v}</dd>
  </div>
);

const DimBox = ({ k, v, unit }) => (
  <div className="border border-neutral-800 p-2">
    <p className="font-mono-label text-[9px] text-neutral-500">{k}</p>
    <p className="font-display font-semibold text-sm mt-1">
      {v == null || v === "" ? "—" : `${v}${unit ? ` ${unit}` : ""}`}
    </p>
  </div>
);

// ---- Generic JSON editor row wrapper ----
function TextInput({ value, onChange, label, testid, type = "text" }) {
  return (
    <label className="block">
      <span className="font-mono-label text-[10px] text-neutral-500">{label}</span>
      <input
        type={type}
        data-testid={testid}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 bg-transparent border-b border-neutral-700 focus:border-[#F5F5F0] py-2 outline-none text-sm"
      />
    </label>
  );
}

// ---- Sim Options ----
function SimOptionsManager() {
  const [options, setOptions] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/sim-options").then((r) => setOptions(r.data));
  useEffect(load, []);

  const blank = () => ({
    id: crypto.randomUUID(),
    slug: "new-component",
    label: "New Component",
    hotspot_x: 50,
    hotspot_y: 50,
    order: options.length + 1,
    tiers: ["Good", "Better", "Best"].map((t) => ({
      tier: t,
      name: `${t} option`,
      price: 1000,
      description: "",
      specs: [],
    })),
  });

  const save = async () => {
    try {
      await api.put(`/admin/sim-options/${editing.id}`, editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/sim-options/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex justify-between mb-6">
        <h1 className="font-display font-black text-3xl tracking-tighter">Sim Builder Options</h1>
        <button
          onClick={() => setEditing(blank())}
          data-testid="sim-option-new-btn"
          className="inline-flex items-center gap-2 bg-[#F5F5F0] text-black px-4 py-2 text-sm font-semibold"
        >
          <Plus size={14} /> New
        </button>
      </div>
      <div className="grid gap-3">
        {options.map((o) => (
          <div key={o.id} className="border border-neutral-900 p-4 flex justify-between items-center">
            <div>
              <p className="font-mono-label text-[10px] text-neutral-500">0{o.order} · {o.slug}</p>
              <p className="font-display font-semibold">{o.label}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(o)} data-testid={`sim-edit-${o.slug}`} className="text-neutral-400 hover:text-[#F5F5F0]"><Edit3 size={16} /></button>
              <button onClick={() => del(o.id)} className="text-neutral-500 hover:text-red-400"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-black border border-neutral-800 w-full max-w-2xl p-6 my-8">
            <div className="flex justify-between mb-6">
              <h3 className="font-display font-bold text-xl">Edit Component</h3>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <TextInput label="Slug" testid="edit-slug" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} />
              <TextInput label="Label" testid="edit-label" value={editing.label} onChange={(v) => setEditing({ ...editing, label: v })} />
              <TextInput type="number" label="Order" value={editing.order} onChange={(v) => setEditing({ ...editing, order: parseInt(v || 0) })} />
              <TextInput type="number" label="Hotspot X (%)" value={editing.hotspot_x} onChange={(v) => setEditing({ ...editing, hotspot_x: parseFloat(v || 0) })} />
              <TextInput type="number" label="Hotspot Y (%)" value={editing.hotspot_y} onChange={(v) => setEditing({ ...editing, hotspot_y: parseFloat(v || 0) })} />
            </div>
            <div className="mt-6 space-y-4">
              {editing.tiers.map((t, i) => (
                <div key={i} className="border border-neutral-900 p-4">
                  <p className="font-mono-label text-[10px] text-neutral-500 mb-3">{t.tier}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <TextInput label="Name" value={t.name} onChange={(v) => {
                      const tiers = [...editing.tiers]; tiers[i] = { ...t, name: v }; setEditing({ ...editing, tiers });
                    }} />
                    <TextInput type="number" label="Price" value={t.price} onChange={(v) => {
                      const tiers = [...editing.tiers]; tiers[i] = { ...t, price: parseInt(v || 0) }; setEditing({ ...editing, tiers });
                    }} />
                  </div>
                  <TextInput label="Description" value={t.description} onChange={(v) => {
                    const tiers = [...editing.tiers]; tiers[i] = { ...t, description: v }; setEditing({ ...editing, tiers });
                  }} />
                  <TextInput label="Specs (comma-separated)" value={(t.specs || []).join(", ")} onChange={(v) => {
                    const tiers = [...editing.tiers]; tiers[i] = { ...t, specs: v.split(",").map((s) => s.trim()).filter(Boolean) }; setEditing({ ...editing, tiers });
                  }} />
                </div>
              ))}
            </div>
            <button
              onClick={save}
              data-testid="sim-option-save-btn"
              className="mt-6 w-full bg-[#F5F5F0] text-black py-3 font-semibold inline-flex items-center justify-center gap-2"
            >
              <Save size={14} /> Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Portfolio Manager ----
function PortfolioManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const load = () => api.get("/admin/portfolio").then((r) => setItems(r.data));
  useEffect(load, []);
  const blank = () => ({
    id: crypto.randomUUID(),
    title: "New Build",
    space_type: "Basement",
    city: "",
    image_url: "",
    notes: "",
    hidden: false,
    order: items.length + 1,
  });
  const save = async () => {
    await api.put(`/admin/portfolio/${editing.id}`, editing);
    toast.success("Saved");
    setEditing(null);
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/portfolio/${id}`);
    load();
  };
  const toggleHide = async (i) => {
    await api.put(`/admin/portfolio/${i.id}`, { ...i, hidden: !i.hidden });
    load();
  };
  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File exceeds 10 MB limit.");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/uploads/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = `${API.replace(/\/api$/, "")}${data.url}`;
      setEditing((e) => ({ ...e, image_url: url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <div className="flex justify-between mb-6">
        <h1 className="font-display font-black text-3xl tracking-tighter">Portfolio</h1>
        <button onClick={() => setEditing(blank())} data-testid="portfolio-new-btn" className="inline-flex items-center gap-2 bg-[#F5F5F0] text-black px-4 py-2 text-sm font-semibold">
          <Plus size={14} /> New
        </button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((i) => (
          <div key={i.id} className="border border-neutral-900">
            <div className="aspect-[4/3] bg-neutral-950">
              {i.image_url && <img src={i.image_url} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="p-4">
              <p className="font-mono-label text-[10px] text-neutral-500">{i.space_type} · {i.city}</p>
              <p className="font-display font-semibold mt-1">{i.title}</p>
              <div className="mt-3 flex gap-2">
                <button onClick={() => setEditing(i)} className="text-neutral-400 hover:text-[#F5F5F0]"><Edit3 size={14} /></button>
                <button onClick={() => toggleHide(i)} className="text-neutral-400 hover:text-[#F5F5F0]">
                  {i.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <button onClick={() => del(i.id)} className="text-neutral-500 hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-black border border-neutral-800 w-full max-w-lg p-6 my-8">
            <div className="flex justify-between mb-6">
              <h3 className="font-display font-bold text-xl">Edit Build</h3>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <TextInput label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
              <TextInput label="City" value={editing.city} onChange={(v) => setEditing({ ...editing, city: v })} />
              <label className="block">
                <span className="font-mono-label text-[10px] text-neutral-500">Space Type</span>
                <select value={editing.space_type} onChange={(e) => setEditing({ ...editing, space_type: e.target.value })} className="w-full mt-1 bg-black border-b border-neutral-700 py-2 text-sm">
                  {["Basement", "Garage", "Shop", "Shed", "Commercial"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </label>
              <TextInput label="Image URL" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} />
              <div className="flex flex-col gap-2">
                <label className="inline-flex items-center justify-center gap-2 border border-dashed border-neutral-700 py-3 px-4 cursor-pointer hover:border-[#F5F5F0] transition-colors text-sm">
                  <input
                    type="file"
                    accept="image/*,image/heic,image/heif"
                    className="hidden"
                    data-testid="portfolio-upload-input"
                    disabled={uploading}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      uploadFile(f);
                      e.target.value = "";
                    }}
                  />
                  <Upload size={14} />
                  {uploading ? "Uploading…" : "Upload photo (10 MB max)"}
                </label>
                {editing.image_url && (
                  <div className="aspect-video overflow-hidden border border-neutral-800">
                    <img src={editing.image_url} alt="preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
              <TextInput label="Notes" value={editing.notes} onChange={(v) => setEditing({ ...editing, notes: v })} />
              <TextInput type="number" label="Order" value={editing.order} onChange={(v) => setEditing({ ...editing, order: parseInt(v || 0) })} />
            </div>
            <button onClick={save} className="mt-6 w-full bg-[#F5F5F0] text-black py-3 font-semibold">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Testimonials Manager ----
function TestimonialsManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data));
  useEffect(load, []);
  const blank = () => ({
    id: crypto.randomUUID(),
    first_name: "",
    city: "",
    build_type: "",
    quote: "",
    hidden: false,
    order: items.length + 1,
  });
  const save = async () => {
    await api.put(`/admin/testimonials/${editing.id}`, editing);
    toast.success("Saved");
    setEditing(null);
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    load();
  };
  return (
    <div>
      <div className="flex justify-between mb-6">
        <h1 className="font-display font-black text-3xl tracking-tighter">Testimonials</h1>
        <button onClick={() => setEditing(blank())} data-testid="testimonial-new-btn" className="inline-flex items-center gap-2 bg-[#F5F5F0] text-black px-4 py-2 text-sm font-semibold">
          <Plus size={14} /> New
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} className="border border-neutral-900 p-5">
            <p className="text-sm text-neutral-300">“{t.quote}”</p>
            <p className="mt-3 font-mono-label text-[10px] text-neutral-500">
              {t.first_name} · {t.city} · {t.build_type}
            </p>
            <div className="mt-3 flex gap-2">
              <button onClick={() => setEditing(t)} className="text-neutral-400 hover:text-[#F5F5F0]"><Edit3 size={14} /></button>
              <button onClick={() => del(t.id)} className="text-neutral-500 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-black border border-neutral-800 w-full max-w-lg p-6 my-8">
            <div className="flex justify-between mb-6">
              <h3 className="font-display font-bold text-xl">Edit Testimonial</h3>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <TextInput label="First Name" value={editing.first_name} onChange={(v) => setEditing({ ...editing, first_name: v })} />
              <TextInput label="City" value={editing.city} onChange={(v) => setEditing({ ...editing, city: v })} />
              <TextInput label="Build Type" value={editing.build_type} onChange={(v) => setEditing({ ...editing, build_type: v })} />
              <label className="block">
                <span className="font-mono-label text-[10px] text-neutral-500">Quote</span>
                <textarea rows="4" value={editing.quote} onChange={(e) => setEditing({ ...editing, quote: e.target.value })} className="w-full mt-1 bg-transparent border-b border-neutral-700 py-2 text-sm resize-none" />
              </label>
            </div>
            <button onClick={save} className="mt-6 w-full bg-[#F5F5F0] text-black py-3 font-semibold">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Savings Editor ----
function SavingsEditor() {
  const [tiers, setTiers] = useState([]);
  useEffect(() => {
    api.get("/config/savings").then((r) => setTiers(r.data.tiers || []));
  }, []);
  const save = async () => {
    await api.put("/admin/config/savings", { tiers });
    toast.success("Saved");
  };
  return (
    <div>
      <h1 className="font-display font-black text-3xl tracking-tighter mb-6">Savings Snapshot</h1>
      <div className="space-y-4">
        {tiers.map((t, i) => (
          <div key={i} className="border border-neutral-900 p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
            <TextInput label="Name" value={t.name} onChange={(v) => { const c = [...tiers]; c[i] = { ...t, name: v }; setTiers(c); }} />
            <TextInput label="Industry" value={t.industry} onChange={(v) => { const c = [...tiers]; c[i] = { ...t, industry: v }; setTiers(c); }} />
            <TextInput label="Sim Guys" value={t.sim_guys} onChange={(v) => { const c = [...tiers]; c[i] = { ...t, sim_guys: v }; setTiers(c); }} />
            <TextInput label="Savings" value={t.savings} onChange={(v) => { const c = [...tiers]; c[i] = { ...t, savings: v }; setTiers(c); }} />
          </div>
        ))}
      </div>
      <button onClick={save} data-testid="savings-save-btn" className="mt-6 bg-[#F5F5F0] text-black px-6 py-3 font-semibold">Save Savings</button>
    </div>
  );
}

// ---- Story Editor ----
function StoryEditor() {
  const [story, setStory] = useState({ title: "", body: "" });
  useEffect(() => {
    api.get("/config/story").then((r) => setStory(r.data));
  }, []);
  const save = async () => {
    await api.put("/admin/config/story", story);
    toast.success("Saved");
  };
  return (
    <div>
      <h1 className="font-display font-black text-3xl tracking-tighter mb-6">Story Copy</h1>
      <div className="space-y-4 max-w-2xl">
        <TextInput label="Title" testid="story-title-input" value={story.title} onChange={(v) => setStory({ ...story, title: v })} />
        <label className="block">
          <span className="font-mono-label text-[10px] text-neutral-500">Body</span>
          <textarea
            data-testid="story-body-input"
            rows="8"
            value={story.body}
            onChange={(e) => setStory({ ...story, body: e.target.value })}
            className="w-full mt-1 bg-transparent border-b border-neutral-700 py-2 text-sm resize-none"
          />
        </label>
      </div>
      <button onClick={save} data-testid="story-save-btn" className="mt-6 bg-[#F5F5F0] text-black px-6 py-3 font-semibold">Save Story</button>
    </div>
  );
}
